import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import { ArrowUpRight, ArrowDownRight, TrendingUp, DollarSign, BarChart2, ExternalLink, Search, RefreshCw, AlertCircle } from 'lucide-react';
import './FinancialAnalyzer.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_KEY = 'FX73FMQ141BQWWST';

const FinancialAnalyzer = () => {
  const [stockData, setStockData] = useState(null);
  const [companyOverview, setCompanyOverview] = useState(null);
  const [symbol, setSymbol] = useState('AAPL');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('30');
  const [watchlist, setWatchlist] = useState(() => {
    const saved = localStorage.getItem('watchlist');
    return saved ? JSON.parse(saved) : ['AAPL', 'GOOGL', 'MSFT','AMZN','TSLA','META','NVDA',"FIX",'DECK'];
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Chart theme options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#94a3b8',
          font: {
            family: 'Plus Jakarta Sans'
          }
        }
      },
      title: {
        display: true,
        text: `${symbol} Stock Price (Last ${timeframe} Days)`,
        color: '#ffffff',
        font: {
          family: 'Plus Jakarta Sans',
          size: 16,
          weight: 600
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 17, 21, 0.9)',
        titleFont: {
          family: 'Plus Jakarta Sans'
        },
        bodyFont: {
          family: 'Plus Jakarta Sans'
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          color: '#94a3b8'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.05)'
        },
        ticks: {
          color: '#94a3b8'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Price ($)',
          color: '#94a3b8'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.05)'
        },
        ticks: {
          color: '#94a3b8'
        }
      }
    }
  }), [symbol, timeframe]);

  const prepareChartData = useCallback((data) => {
    const dates = Object.keys(data).slice(0, parseInt(timeframe)).reverse();
    const prices = dates.map(date => parseFloat(data[date]['4. close']));
    

    // Calculate moving averages
    const ma20 = calculateMA(prices, 20);
    const ma50 = calculateMA(prices, 50);

    return {
      labels: dates,
      datasets: [
        {
          label: 'Stock Price',
          data: prices,
          borderColor: '#0066ff',
          backgroundColor: 'rgba(0, 102, 255, 0.1)',
          tension: 0.4,
          fill: true,
          borderWidth: 2
        },
        {
          label: '20-Day MA',
          data: ma20,
          borderColor: '#10b981',
          borderWidth: 1.5,
          borderDash: [5, 5],
          fill: false,
          tension: 0.4
        },
        {
          label: '50-Day MA',
          data: ma50,
          borderColor: '#f59e0b',
          borderWidth: 1.5,
          borderDash: [5, 5],
          fill: false,
          tension: 0.4
        }
      ]
    };
  }, [timeframe]);

  const calculateMA = (data, period) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push(null);
        continue;
      }
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += data[i - j];
      }
      result.push(sum / period);
    }
    return result;
  };
  const fetchStockData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [timeSeriesResponse, overviewResponse] = await Promise.all([
        axios.get(`https://www.alphavantage.co/query?function=TIME_SERIES_DAILY&symbol=${symbol}&apikey=${API_KEY}`),
        axios.get(`https://www.alphavantage.co/query?function=OVERVIEW&symbol=${symbol}&apikey=${API_KEY}`)
      ]);
  
      console.log('Time Series Response:', timeSeriesResponse.data);
      console.log('Overview Response:', overviewResponse.data);
  
      const timeSeries = timeSeriesResponse.data['Time Series (Daily)'];
      if (!timeSeries) {
        throw new Error('No time series data received from API');
      }
      setStockData(timeSeries);
      setChartData(prepareChartData(timeSeries));
  
      const overview = overviewResponse.data;
      if (!overview || Object.keys(overview).length === 0) {
        throw new Error('No company overview data received from API');
      }
      setCompanyOverview(overview);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error fetching stock data:', error);
      setError(`Failed to fetch stock data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [symbol, prepareChartData]);

  useEffect(() => {
    fetchStockData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStockData, 60000); // Refresh every minute
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchStockData, autoRefresh]);

  useEffect(() => {
    localStorage.setItem('watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStockData();
  };

  const toggleWatchlist = useCallback(() => {
    setWatchlist(prev => {
      if (prev.includes(symbol)) {
        return prev.filter(s => s !== symbol);
      } else {
        return [...prev, symbol];
      }
    });
  }, [symbol]);

  // ... rest of your render methods (renderStockInfo, renderAnalysis) ...
  const renderStockInfo = () => {
    if (!stockData || !companyOverview) return null;
    
    const dates = Object.keys(stockData);
    if (dates.length < 2) return null;
    
    const latestDate = dates[0];
    const previousDate = dates[1];
    const latestData = stockData[latestDate];
    const previousData = stockData[previousDate];
    
    const currentPrice = parseFloat(latestData['4. close']);
    const previousClose = parseFloat(previousData['4. close']);
    const priceChange = currentPrice - previousClose;
    const priceChangePercentage = (priceChange / previousClose * 100).toFixed(2);
  
    return (
      <div className="stock-info">
        <h2>{symbol} - {companyOverview.Name}</h2>
        <p className="current-price">${currentPrice.toFixed(2)}</p>
        <p className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
          {priceChange >= 0 ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}
          ${Math.abs(priceChange).toFixed(2)} ({priceChangePercentage}%)
        </p>
        <div className="key-stats">
          <div className="stat">
            <TrendingUp size={20} />
            <span>Previous Close</span>
            <strong>${previousClose.toFixed(2)}</strong>
          </div>
          <div className="stat">
            <DollarSign size={20} />
            <span>Market Cap</span>
            <strong>${parseFloat(companyOverview.MarketCapitalization).toLocaleString()}</strong>
          </div>
          <div className="stat">
            <BarChart2 size={20} />
            <span>P/E Ratio</span>
            <strong>{companyOverview.PERatio}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  const renderAnalysis = () => {
    if (!companyOverview) return null;
  
    const pe = parseFloat(companyOverview.PERatio);
    const industryPE = parseFloat(companyOverview.PEGRatio);
    const beta = parseFloat(companyOverview.Beta);
  
    return (
      <div className="analysis">
        <h3>Analysis</h3>
        <p>
          {companyOverview.Name} operates in the {companyOverview.Industry} industry. 
          The company has a market capitalization of ${parseFloat(companyOverview.MarketCapitalization).toLocaleString()}, 
          placing it in the {companyOverview.MarketCapitalization > 10000000000 ? 'large' : 'mid'}-cap category.
        </p>
        <p>
          With a P/E ratio of {pe.toFixed(2)}, the stock is trading 
          {pe > industryPE ? ' above ' : ' below '} 
          the industry average of {industryPE.toFixed(2)}. This suggests the stock is 
          {pe > industryPE ? ' potentially overvalued' : ' potentially undervalued'} 
          compared to its peers.
        </p>
        <p>
          The stock's beta of {beta.toFixed(2)} indicates that it is 
          {beta > 1 ? ' more volatile ' : ' less volatile '} 
          than the overall market. Investors should be prepared for 
          {beta > 1 ? ' higher risk and potentially higher returns' : ' lower risk but potentially lower returns'}.
        </p>
        <p>
          The company's dividend yield is {companyOverview.DividendYield ? 
            `${(parseFloat(companyOverview.DividendYield) * 100).toFixed(2)}%, which may be attractive for income-focused investors.` : 
            'not available, suggesting the company may not pay dividends or reinvests its earnings for growth.'}
        </p>
      </div>
    );
  };
  return (
    <div className="financial-analyzer">
      <div className="top-bar">
        <div className="header-content">
          
          <div className="last-updated">
            {lastUpdated && (
              <span>Last updated: {lastUpdated}</span>
            )}
            <div className="auto-refresh">
              <label>
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={() => setAutoRefresh(prev => !prev)}
                />
                Auto-refresh
              </label>
            </div>
          </div>
        </div>
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-bar">
            <Search size={20} />
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="Enter stock symbol"
            />
            <button type="submit" disabled={loading}>
              {loading ? <RefreshCw className="spin" /> : 'Analyze'}
            </button>
          </form>
          <div className="watchlist">
            <h4>Watchlist</h4>
            <div className="watchlist-items">
              {watchlist.map(sym => (
                <button
                  key={sym}
                  onClick={() => setSymbol(sym)}
                  className={sym === symbol ? 'active' : ''}
                >
                  {sym}
                </button>
              ))}
            </div>
            <button
              onClick={toggleWatchlist}
              className={`watch-button ${watchlist.includes(symbol) ? 'watched' : ''}`}
            >
              {watchlist.includes(symbol) ? 'Remove from Watchlist' : 'Add to Watchlist'}
            </button>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="main-content">
        <div className="left-panel">
          {renderStockInfo()}
          {renderAnalysis()}
          <a href="https://swanpoint.streamlit.app/" target="_blank" rel="noopener noreferrer" className="detailed-analysis-link">
            Swanpoint Screener <ExternalLink size={16} />
          </a>
        </div>
        <div className="right-panel">
          <div className="chart-controls">
            <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
              <option value="7">1 Week</option>
              <option value="30">1 Month</option>
              <option value="90">3 Months</option>
              <option value="180">6 Months</option>
              <option value="365">1 Year</option>
            </select>
          </div>
          <div className="chart">
            {chartData && (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalyzer;
  